/* =============================================================================
   PREVIEW MODE  —  test the UI with no backend / no Auth0.
   -----------------------------------------------------------------------------
   Toggle:
     …?preview=1   turn ON  (sticky)
     …?preview=0   turn OFF
   Preview is OFF unless someone asks for it with ?preview=1 - localhost included.
   (It briefly switched itself on for localhost. That silently replaced every backend
   call with fixtures and re-created the fake user 999 after each logout, so anyone
   developing against a real backend could not use the app. Do not make it default-on
   again without a way to opt out that survives logout - see preview.util.spec.ts.)
   When ON:
     • a fake signed-in user is seeded → auth guards pass
     • PreviewInterceptor answers the data endpoints with fixtures
     • Login / /auth0 / logout never bounce to Auth0
   ============================================================================= */

const FLAG = 'wt_preview';

const FAKE_USER = {
  id: 999,
  name: 'Preview GM',
  first_name: 'Preview',
  last_name: 'GM',
  display_name: 'Preview GM',
  email: 'preview@worldtumbler.test',
  user_name: 'preview_gm',
  image: null,
  privacy_status: true,
  role: 1,
};

const FAKE_PLAN = {
  plan: 'worldsmith',
  subscription_features: {
    can_journal: true,
    delegate_journal: true,
    access_wiki: true,
    wiki_ownership: true,
    delegate_wiki: true,
    create_modules: true,
    favorite_content: true,
    create_games: true,
    can_join_games: true,
    max_games_created: -1,
    party_members: -1,
    max_active_characters: -1,
    max_retired_npcs: -1,
    delete_maps: true,
    upload_maps: true,
    maps_uploaded: -1,
    access_wt_map: true,
    ai_token_generated: 999,
  },
  usage: {
    id: 1,
    characters_created: 4,
    retired_npcs_created: 2,
    maps_uploaded: 2,
    party_members_created: 3,
    journals_created: 6,
    games_joined: 1,
    games_created: 3,
    created_at: '2025-01-10T00:00:00Z',
    updated_at: '2026-09-10T00:00:00Z',
    tokens_used: 12,
    user: 999,
  },
};

export function isPreviewMode(): boolean {
  try {
    return localStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

export function previewUser(): typeof FAKE_USER {
  return { ...FAKE_USER };
}

export function previewPlan(): typeof FAKE_PLAN {
  return JSON.parse(JSON.stringify(FAKE_PLAN));
}

export function seedPreviewAuth(): void {
  try {
    localStorage.setItem(FLAG, '1');
    localStorage.setItem('currentUser', JSON.stringify(FAKE_USER));
    sessionStorage.setItem('planUsage', JSON.stringify(FAKE_PLAN));
    sessionStorage.setItem('token', 'preview-token');
  } catch {
    /* ignore storage failures */
  }
}

function clearPreviewAuth(): void {
  try {
    localStorage.setItem(FLAG, '0');
    const u = localStorage.getItem('currentUser');
    if (u && JSON.parse(u)?.email === FAKE_USER.email) {
      localStorage.removeItem('currentUser');
    }
    sessionStorage.removeItem('planUsage');
    sessionStorage.removeItem('token');
  } catch {
    /* ignore */
  }
}

function stripPreviewParam(params: URLSearchParams): void {
  params.delete('preview');
  const qs = params.toString();
  const url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
  window.history.replaceState({}, '', url);
}

/** Run once from main.ts, before Angular bootstraps. */
export function syncPreviewFromUrl(): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  let explicit: boolean | null = null;
  if (params.has('preview')) {
    const v = (params.get('preview') || '').toLowerCase();
    explicit = v === '' || v === '1' || v === 'true' || v === 'on' || v === 'yes';
    stripPreviewParam(params);
  }

  if (explicit === false) {
    clearPreviewAuth();
    return;
  }

  // Only an explicit ?preview=1 turns it on (an already-sticky flag keeps it on).
  if (explicit === true) {
    seedPreviewAuth();
  }
}
